// client/src/pages/business/OrderTemplates.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Search,
  Plus,
  Copy,
  Trash2,
  Edit,
  PlayCircle,
  Calendar,
  Package,
  Filter,
  X,
  ChevronDown,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import StorefrontBanner from '../../components/common/StorefrontBanner';
import { orderTemplatesAPI } from '../../services/orderTemplatesAPI';
import { orderTemplatesAPI as orderTemplatesAPIBulk } from '../../services/api';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import CreateTemplateModal from '../../components/templates/CreateTemplateModal';
import UseTemplateModal from '../../components/templates/UseTemplateModal';
import EditTemplateModal from '../../components/templates/EditTemplateModal';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

const OrderTemplates = () => {
  const { t } = useTranslation('business');
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1024px)');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWholesaler, setSelectedWholesaler] = useState('all');
  const [sortBy, setSortBy] = useState('lastUsed');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, icon: 'warning', confirmColor: 'red' });
  const closeConfirmModal = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    fetchTemplates();
  }, [sortBy, sortOrder]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await orderTemplatesAPI.getAll({ sortBy, sortOrder });
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setConfirmModal({
        isOpen: true,
        title: t('templates.error', 'Error'),
        message: t('templates.failedToLoad'),
        icon: 'info',
        confirmLabel: t('common:confirm'),
        confirmColor: 'blue',
        onConfirm: closeConfirmModal
      });
    } finally {
      setLoading(false);
    }
  };

  // Get unique wholesalers from templates (now from items)
  const wholesalers = [...new Set(
    templates
      .flatMap(t => t.items || [])
      .filter(item => item.wholesalerId)
      .map(item => JSON.stringify({
        id: item.wholesalerId._id || item.wholesalerId,
        name: item.wholesalerId.companyName || 'Unknown'
      }))
  )].map(str => JSON.parse(str));

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesWholesaler =
      selectedWholesaler === 'all' ||
      template.items?.some(item =>
        (item.wholesalerId?._id || item.wholesalerId) === selectedWholesaler
      );

    return matchesSearch && matchesWholesaler;
  });

  // Bulk selection
  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect
  } = useRangeSelect(filteredTemplates);

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    setShowUseModal(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowEditModal(true);
  };

  const handleDuplicateTemplate = async (template) => {
    setConfirmModal({
      isOpen: true,
      title: t('templates.duplicateConfirmTitle', 'Duplicate Template'),
      message: t('templates.duplicateConfirm', { name: template.name }),
      icon: 'info',
      confirmLabel: t('templates.actions.duplicate', 'Duplicate'),
      confirmColor: 'blue',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await orderTemplatesAPI.duplicate(template._id);
          fetchTemplates();
        } catch (error) {
          console.error('Error duplicating template:', error);
          setConfirmModal({
            isOpen: true,
            title: t('templates.error', 'Error'),
            message: t('templates.duplicateError'),
            icon: 'info',
            confirmLabel: t('common:confirm'),
            confirmColor: 'blue',
            onConfirm: closeConfirmModal
          });
        }
      }
    });
  };

  const handleDeleteTemplate = async (template) => {
    setConfirmModal({
      isOpen: true,
      title: t('templates.deleteConfirmTitle', 'Delete Template'),
      message: t('templates.deleteConfirmFull', { name: template.name }),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await orderTemplatesAPI.delete(template._id);
          fetchTemplates();
        } catch (error) {
          console.error('Error deleting template:', error);
          setConfirmModal({
            isOpen: true,
            title: t('templates.error', 'Error'),
            message: t('templates.deleteError'),
            icon: 'info',
            confirmLabel: t('common:confirm'),
            confirmColor: 'blue',
            onConfirm: closeConfirmModal
          });
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    setConfirmModal({
      isOpen: true,
      title: t('templates.bulkDeleteTitle', 'Delete Selected Templates'),
      message: t('templates.bulkDeleteMessage', 'Are you sure you want to delete {{count}} selected templates?', { count }),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirmModal();
        try {
          await orderTemplatesAPIBulk.bulkDelete([...selectedIds]);
          clearSelection();
          fetchTemplates();
        } catch (error) {
          console.error('Error bulk deleting templates:', error);
          setConfirmModal({
            isOpen: true,
            title: t('templates.error', 'Error'),
            message: t('templates.bulkDeleteError', 'Failed to delete selected templates.'),
            icon: 'info',
            confirmLabel: t('common:confirm'),
            confirmColor: 'blue',
            onConfirm: closeConfirmModal
          });
        }
      }
    });
  };

  const formatDate = (date) => {
    if (!date) return t('templates.never');
    return new Date(date).toLocaleDateString('el-GR', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFrequencyBadge = (frequency) => {
    const badges = {
      weekly: { text: t('templates.weekly'), color: 'bg-blue-100 text-blue-800' },
      biweekly: { text: t('templates.biweekly'), color: 'bg-purple-100 text-purple-800' },
      monthly: { text: t('templates.monthly'), color: 'bg-green-100 text-green-800' },
      custom: { text: t('templates.custom'), color: 'bg-gray-100 text-gray-800' }
    };
    return badges[frequency] || badges.custom;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('templates.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-8 h-8 text-blue-600" />
                {t('templates.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('templates.subtitle')}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {!isMobile && t('templates.newTemplate')}
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600">{t('templates.totalTemplates')}</p>
              <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600">{t('templates.active')}</p>
              <p className="text-2xl font-bold text-green-600">
                {templates.filter(t => t.isActive).length}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600">{t('templates.totalUses')}</p>
              <p className="text-2xl font-bold text-blue-600">
                {templates.reduce((sum, t) => sum + t.timesUsed, 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600">{t('templates.wholesalersCount')}</p>
              <p className="text-2xl font-bold text-purple-600">{wholesalers.length}</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('templates.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Mobile Filter Toggle */}
              {isMobile && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Filter className="w-5 h-5" />
                  {showFilters ? t('templates.hideFilters') : t('templates.showFilters')}
                </button>
              )}
            </div>

            {/* Filters - Show on desktop or when toggled on mobile */}
            {(!isMobile || showFilters) && (
              <div className="flex flex-col md:flex-row gap-4 pt-4 border-t border-gray-200">
                {/* Wholesaler Filter */}
                <select
                  value={selectedWholesaler}
                  onChange={(e) => setSelectedWholesaler(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">{t('templates.allWholesalers')}</option>
                  {wholesalers.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>

                {/* Sort By */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="lastUsed">{t('templates.lastUsed')}</option>
                  <option value="timesUsed">{t('templates.mostUsed')}</option>
                  <option value="name">{t('templates.name')}</option>
                  <option value="createdAt">{t('templates.dateCreated')}</option>
                </select>

                {/* Sort Order */}
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="desc">{t('templates.descending')}</option>
                  <option value="asc">{t('templates.ascending')}</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        {filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || selectedWholesaler !== 'all' ? t('templates.noTemplatesFound') : t('templates.noTemplatesYet')}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || selectedWholesaler !== 'all'
                ? t('templates.adjustFilters')
                : t('templates.saveTimeOrders')}
            </p>
            {!searchTerm && selectedWholesaler === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                {t('templates.createTemplate')}
              </button>
            )}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            {...dragProps}
            ref={dragProps.ref}
            style={{ userSelect: isDragging ? 'none' : undefined }}
          >
            <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template._id}
                template={template}
                isSelected={selectedIds.has(template._id)}
                onSelect={handleItemClick}
                onUse={handleUseTemplate}
                onEdit={handleEditTemplate}
                onDuplicate={handleDuplicateTemplate}
                onDelete={handleDeleteTemplate}
                formatDate={formatDate}
                getFrequencyBadge={getFrequencyBadge}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Bar for bulk actions */}
      <FloatingActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onSelectAll={selectAll}
      >
        <button
          onClick={handleBulkDelete}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          {t('templates.bulkDeleteBtn', 'Delete Selected')}
        </button>
      </FloatingActionBar>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirmModal}
      />

      {/* Modals */}
      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTemplates();
          }}
        />
      )}

      {showUseModal && selectedTemplate && (
        <UseTemplateModal
          template={selectedTemplate}
          onClose={() => {
            setShowUseModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={() => {
            setShowUseModal(false);
            setSelectedTemplate(null);
            fetchTemplates();
          }}
        />
      )}

      {showEditModal && selectedTemplate && (
        <EditTemplateModal
          template={selectedTemplate}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedTemplate(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

// Template Card Component
const TemplateCard = ({
  template,
  isSelected,
  onSelect,
  onUse,
  onEdit,
  onDuplicate,
  onDelete,
  formatDate,
  getFrequencyBadge,
  t
}) => {
  const frequencyBadge = getFrequencyBadge(template.frequency);

  // Get unique wholesalers from template items
  const uniqueWholesalers = template.items?.reduce((acc, item) => {
    const wholesalerId = item.wholesalerId?._id || item.wholesalerId;
    const wholesalerName = item.wholesalerId?.companyName || 'Unknown';
    if (wholesalerId && !acc.find(w => w.id === wholesalerId)) {
      acc.push({ id: wholesalerId, name: wholesalerName });
    }
    return acc;
  }, []) || [];

  return (
    <div
      data-select-id={template._id}
      className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 relative ${
        isSelected ? 'ring-2 ring-indigo-400 border-indigo-300' : ''
      }`}
    >
      {/* Selection checkbox — top-right */}
      <button
        onClick={(e) => { e.stopPropagation(); onSelect(template._id, e); }}
        className={`absolute top-3 right-3 w-5 h-5 rounded border-2 flex items-center justify-center z-10 transition-colors ${
          isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-indigo-400 bg-white'
        }`}
      >
        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
      </button>

      {/* Header */}
      <div className="mb-4 pr-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 flex-1 pr-2">
            {template.name}
          </h3>
          <span className={`px-2 py-1 rounded text-xs font-medium ${frequencyBadge.color}`}>
            {frequencyBadge.text}
          </span>
        </div>
        {template.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
        )}
      </div>

      {/* Wholesalers */}
      <div className="mb-4 pb-4 border-b border-gray-200">
        {uniqueWholesalers.length === 1 ? (
          <p className="text-sm text-gray-600">
            <span className="font-medium">{t('templates.wholesaler')}:</span>{' '}
            {uniqueWholesalers[0].name}
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            <span className="font-medium">{t('templates.wholesalersCount')}:</span>{' '}
            <span className="text-blue-600 font-semibold">{uniqueWholesalers.length}</span>
            {' '}({uniqueWholesalers.map(w => w.name).join(', ')})
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-gray-500">{t('templates.items', { count: template.items.length })}</p>
          <p className="text-lg font-semibold text-gray-900 flex items-center gap-1">
            <Package className="w-4 h-4" />
            {template.items.length}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('templates.timesUsed')}</p>
          <p className="text-lg font-semibold text-blue-600">
            {template.timesUsed}
          </p>
        </div>
      </div>

      {/* Last Used */}
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        {t('templates.lastUsedDate')}: {formatDate(template.lastUsed)}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onUse(template)}
          className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium flex items-center justify-center gap-1"
        >
          <PlayCircle className="w-4 h-4" />
          {t('business:templates.actions.use')}
        </button>
        <button
          onClick={() => onEdit(template)}
          className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition text-sm font-medium flex items-center justify-center gap-1"
        >
          <Edit className="w-4 h-4" />
          {t('business:templates.actions.edit')}
        </button>
        <button
          onClick={() => onDuplicate(template)}
          className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition text-sm font-medium flex items-center justify-center gap-1"
        >
          <Copy className="w-4 h-4" />
          {t('business:templates.actions.duplicate')}
        </button>
        <button
          onClick={() => onDelete(template)}
          className="bg-red-50 text-red-600 px-3 py-2 rounded-lg hover:bg-red-100 transition text-sm font-medium flex items-center justify-center gap-1"
        >
          <Trash2 className="w-4 h-4" />
          {t('business:templates.actions.delete')}
        </button>
      </div>
    </div>
  );
};

export default OrderTemplates;
